import { GoogleAnalytics, GoogleTagManager } from "@next/third-parties/google";

interface GTagProps {
	gtmId?: string;
	gaId?: string;
}

/**
 * Containers and properties that belong to production. Only `prod` may write to
 * them. These are public IDs — they ship in the page HTML — so naming them here
 * costs nothing and keeps the rule readable.
 */
const PRODUCTION_IDS = [
	"GTM-NJ2SFKJ", // GIS container
	"G-9VXZ3DTRFB", // GIS GA4 property
	"GTM-NRD598Q5", // franchise container
	"G-L3JFP01NQK", // franchise GA4 property
];

/**
 * Renders Google Tag Manager and Google Analytics, but refuses to do so when a
 * non-production stage has been pointed at a production container or property.
 *
 * The GTM triggers filter only on `Page Hostname contains mangomap.com`, and
 * the deployed non-production stages are also `*.mangomap.com`, so nothing
 * downstream catches a mis-set environment variable — dev traffic simply lands
 * in the production dataset. This makes that failure mode fail closed.
 *
 * Also skips rendering when the IDs are absent (local development), which
 * avoids requesting `gtm.js?id=undefined`.
 */
export function TagManager({ gtmId, gaId }: GTagProps): JSX.Element | null {
	const isProduction = process.env.SST_STAGE === "prod";
	const usesProductionId =
		PRODUCTION_IDS.includes(gtmId ?? "") || PRODUCTION_IDS.includes(gaId ?? "");

	if (!gtmId || !gaId) return null;
	if (!isProduction && usesProductionId) return null;

	return (
		<>
			<GoogleTagManager gtmId={gtmId} />
			<GoogleAnalytics gaId={gaId} />
		</>
	);
}
